// Alerte « société arrivée » (spec § 11) : depuis le bloc Sociétés d'une fiche, l'utilisateur demande à être prévenu
// quand une société y arrive, sans créer d'affaire. Les familles de rôles se règlent une fois, dans l'onglet
// « Réglages » des alertes, avec la liste des fiches surveillées. La base écrit les alertes (`alert_watched_company`).
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { BellOff, BellPlus, BellRing } from "lucide-react";
import {
  type Identifier,
  useGetIdentity,
  useGetList,
  useGetOne,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Deal, Project, Sale } from "../types";
import { formatDay } from "./projectUtils";
import { projectName } from "./projectText";
import {
  missingFamilies,
  WATCH_FAMILIES,
  type WatchFamily,
} from "./watchFamilies";
import { HelpTip } from "./HelpTip";

type Watch = {
  project_id: string;
  created_at: string;
  projects: { id: string; name: string | null; name_fr: string | null } | null;
};

const WATCHES_KEY = ["project_watches"];

/** Fiches surveillées par l'utilisateur, les plus récentes d'abord (la base ne rend que les siennes). */
const useWatches = () =>
  useQuery({
    queryKey: WATCHES_KEY,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("project_watches")
        .select("project_id, created_at, projects(id, name, name_fr)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Watch[];
    },
  });

const useMySale = () => {
  const { identity } = useGetIdentity();
  return useGetOne<Sale>(
    "sales",
    { id: identity?.id as Identifier },
    { enabled: identity?.id != null },
  );
};

const useWatchActions = () => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const run = async (action: () => PromiseLike<{ error: unknown }>) => {
    setBusy(true);
    const { error } = await action();
    setBusy(false);
    if (error) notify((error as Error).message, { type: "error" });
    queryClient.invalidateQueries({ queryKey: WATCHES_KEY });
    return !error;
  };
  const watch = (projectId: Identifier) =>
    run(() =>
      getSupabaseClient()
        .from("project_watches")
        .insert({ project_id: projectId }),
    );
  const unwatch = (projectId: Identifier) =>
    run(() =>
      getSupabaseClient()
        .from("project_watches")
        .delete()
        .eq("project_id", projectId),
    );
  return { busy, watch, unwatch };
};

const familyList = (
  families: WatchFamily[],
  translate: (key: string) => string,
) =>
  families
    .map((family) => translate(`resources.projects.watch.families.${family}`))
    .join(", ");

/** Pied du bloc Sociétés de la fiche : demander l'alerte, ou la voir active et l'arrêter. */
export const WatchCompanies = ({
  project,
  roles,
  hideMissing = false,
}: {
  project: Project;
  roles: string[];
  // Bloc Contacts : la note de la recherche dit déjà qu'aucune société n'est connue.
  hideMissing?: boolean;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { data: sale } = useMySale();
  const { data: watches, isPending } = useWatches();
  const { busy, watch, unwatch } = useWatchActions();
  const { data: deals } = useGetList<Deal>("deals", {
    filter: { project_id: project.id },
    pagination: { page: 1, perPage: 10 },
  });
  if (!sale || isPending) return null;
  // Une affaire en cours dont il est responsable l'alerte déjà de toute arrivée de société.
  if (deals?.some((deal) => deal.sales_id === sale.id && !deal.archived_at))
    return null;

  const t = (key: string, options?: object) =>
    translate(`resources.projects.watch.${key}`, options);
  const families = (sale.watch_roles ?? []) as WatchFamily[];
  const watching = watches?.some((w) => w.project_id === project.id);
  const missing = missingFamilies(roles, families);

  return (
    <div className="mt-3 flex flex-col gap-2 border-t pt-3 text-sm">
      {watching ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1.5">
            <BellRing className="size-4 text-primary" />
            {t("active", { families: familyList(families, translate) })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            disabled={busy}
            onClick={() => unwatch(project.id)}
          >
            <BellOff className="size-4" />
            {t("stop")}
          </Button>
          <Link
            to="/alerts?tab=settings"
            className="text-muted-foreground underline underline-offset-2"
          >
            {t("settings_link")}
          </Link>
        </div>
      ) : (
        <>
          {missing.length > 0 && !hideMissing && (
            <p className="text-muted-foreground">
              {t("missing", { families: familyList(missing, translate) })}
            </p>
          )}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={async () => {
                if (await watch(project.id)) notify(t("done"));
              }}
            >
              <BellPlus className="size-4" />
              {t("watch")}
            </Button>
            <HelpTip topic="follow" />
          </div>
        </>
      )}
    </div>
  );
};

/** Onglet « Réglages » des alertes : familles de rôles et fiches surveillées. */
export const WatchSettings = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "fr"] = useLocaleState();
  const { data: sale, refetch } = useMySale();
  const { data: watches } = useWatches();
  const { busy, unwatch } = useWatchActions();
  if (!sale) return null;
  const t = (key: string, options?: object) =>
    translate(`resources.projects.watch.${key}`, options);
  const families = (sale.watch_roles ?? []) as WatchFamily[];

  const toggle = async (family: WatchFamily, checked: boolean) => {
    const next = checked
      ? [...families, family]
      : families.filter((value) => value !== family);
    // La base refuse une liste vide : pour ne plus rien recevoir, on arrête les surveillances.
    if (!next.length) {
      notify(t("one_family"), { type: "warning" });
      return;
    }
    const { error } = await getSupabaseClient().rpc("set_watch_roles", {
      roles: next,
    });
    if (error) notify(error.message, { type: "error" });
    refetch();
  };

  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="flex items-center gap-1 font-semibold">
          {t("settings_title")}
          <HelpTip topic="follow" />
        </h3>
        <p className="text-sm text-muted-foreground">{t("settings_intro")}</p>
      </div>
      <div className="flex flex-col gap-2">
        {WATCH_FAMILIES.map((family) => (
          <div key={family} className="flex items-start gap-3">
            <Checkbox
              id={`watch-${family}`}
              checked={families.includes(family)}
              onCheckedChange={(checked) => toggle(family, checked === true)}
              className="mt-0.5"
            />
            <div className="flex flex-col gap-0.5">
              <Label htmlFor={`watch-${family}`}>
                {t(`families.${family}`)}
              </Label>
              <span className="text-xs text-muted-foreground">
                {t(`family_roles.${family}`)}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-1">
        <h4 className="text-sm font-medium">
          {t("watched", { smart_count: watches?.length ?? 0 })}
        </h4>
        {!watches?.length ? (
          <p className="text-sm text-muted-foreground">{t("none")}</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {watches.map((w) => (
              <li
                key={w.project_id}
                className="flex items-center gap-2 px-3 py-2 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/projects/${w.project_id}/show`}
                    className="underline underline-offset-2 line-clamp-1"
                  >
                    {w.projects
                      ? projectName(w.projects as Project, locale)
                      : w.project_id}
                  </Link>
                  <span className="text-xs text-muted-foreground">
                    {t("since", { date: formatDay(w.created_at, locale) })}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => unwatch(w.project_id)}
                >
                  {t("stop")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};
